import {
  AnimationEvent,
  CSSProperties,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { randomBetween, RandomRange } from '../../helpers';
import { getLegMotion, SpiderPhase } from './legMotion';
import { SPIDER_ABDOMEN_PATH, SPIDER_LEGS, SPIDER_PALPS_PATH, WEB_PATH } from './spiderShapes';

import classNames from 'classnames';
import classes from './SpiderWeb.module.scss';

const REST_DELAY_MS: RandomRange = { min: 15000, max: 45000 };

const DANGLE_DURATION_MS: RandomRange = { min: 4000, max: 9000 };

const DROP_DEPTH_VH: RandomRange = { min: 14, max: 26 };

const SETTLED: SettleFrom = { angle: 0, scale: 1, legs: [] };

interface SettleFrom {
  angle: number;
  scale: number;
  legs: number[];
}

const getTransformMatrix = (element: HTMLElement | null) => {
  const transform = element && getComputedStyle(element).transform;

  if (!transform || transform === 'none') {
    return new DOMMatrixReadOnly();
  }

  return new DOMMatrixReadOnly(transform);
};

const getLegAngles = (svg: SVGSVGElement | null) =>
  Array.from(
    svg?.querySelectorAll<SVGPathElement>(`.${classes['spider-web__leg']}`) ?? [],
    ({ transform: { animVal } }) => (animVal.numberOfItems ? animVal.getItem(0).angle : 0)
  );

export const SpiderWeb: FunctionComponent = () => {
  const [phase, setPhase] = useState(SpiderPhase.RESTING);
  const [dropDepth, setDropDepth] = useState(DROP_DEPTH_VH.min);
  const [settleFrom, setSettleFrom] = useState<SettleFrom>(SETTLED);

  const spiderRef = useRef<HTMLDivElement>(null);
  const turnRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const settle = useCallback((nextPhase: SpiderPhase) => {
    const { a: swayCosine, b: swaySine } = getTransformMatrix(spiderRef.current);
    const { a: turnScaleX, b: turnSkewY } = getTransformMatrix(turnRef.current);

    setSettleFrom({
      angle: (Math.atan2(swaySine, swayCosine) * 180) / Math.PI,
      scale: Math.hypot(turnScaleX, turnSkewY),
      legs: getLegAngles(svgRef.current),
    });
    setPhase(nextPhase);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (phase === SpiderPhase.RESTING) {
      timer = setTimeout(() => {
        setDropDepth(randomBetween(DROP_DEPTH_VH));
        settle(SpiderPhase.DROPPING);
      }, randomBetween(REST_DELAY_MS));
    }

    if (phase === SpiderPhase.DANGLING) {
      timer = setTimeout(() => settle(SpiderPhase.CLIMBING), randomBetween(DANGLE_DURATION_MS));
    }

    return () => clearTimeout(timer);
  }, [phase, settle]);

  const animationEndHandler = useCallback(
    ({ target, currentTarget }: AnimationEvent<HTMLDivElement>) => {
      if (target !== currentTarget) {
        return;
      }

      setSettleFrom(SETTLED);
      setPhase((phasePrev) =>
        phasePrev === SpiderPhase.DROPPING ? SpiderPhase.DANGLING : SpiderPhase.RESTING
      );
    },
    []
  );

  const scareHandler = useCallback(() => settle(SpiderPhase.SCARED), [settle]);

  const legs = useMemo(
    () =>
      SPIDER_LEGS.map((leg, index) => {
        const { path, pivot } = leg;
        const startAngle = settleFrom.legs[index] ?? 0;
        const motion = getLegMotion({ leg, index, phase, startAngle });
        const restingPose = `rotate(${startAngle} ${pivot})`;

        if (!motion) {
          return (
            <path
              key={pivot}
              className={classes['spider-web__leg']}
              d={path}
              transform={restingPose}
            />
          );
        }

        const { values, keyTimes, duration, begin, isOneShot } = motion;

        return (
          <path key={pivot} className={classes['spider-web__leg']} d={path} transform={restingPose}>
            <animateTransform
              attributeName={'transform'}
              type={'rotate'}
              values={values.map((angle) => `${angle} ${pivot}`).join(';')}
              keyTimes={keyTimes.join(';')}
              calcMode={'spline'}
              keySplines={keyTimes
                .slice(1)
                .map(() => '0.4 0 0.6 1')
                .join(';')}
              dur={`${duration}s`}
              begin={`${begin}s`}
              repeatCount={isOneShot ? 1 : 'indefinite'}
              fill={isOneShot ? 'freeze' : 'remove'}
            />
          </path>
        );
      }),
    [phase, settleFrom.legs]
  );

  const { angle, scale } = settleFrom;

  return (
    <div>
      <svg
        className={classNames(classes['spider-web__web'], classes[`spider-web__web--${phase}`])}
        viewBox={'0 0 100 100'}
      >
        <path d={WEB_PATH} />
      </svg>
      <div
        ref={spiderRef}
        className={classNames(
          classes['spider-web__spider'],
          classes[`spider-web__spider--${phase}`]
        )}
        style={
          {
            '--drop': `${dropDepth}vh`,
            '--sway-from': `${angle}deg`,
            '--turn-from': scale,
          } as CSSProperties
        }
      >
        <div
          className={classNames(classes['spider-web__drop'], classes[`spider-web__drop--${phase}`])}
          onAnimationEnd={animationEndHandler}
          {...(phase === SpiderPhase.DANGLING && { onClick: scareHandler })}
        >
          <div ref={turnRef} className={classes[`spider-web__turn--${phase}`]}>
            <svg
              key={phase}
              ref={svgRef}
              className={classes['spider-web__body']}
              viewBox={'-15 -15 30 30'}
            >
              {legs}
              <path d={SPIDER_ABDOMEN_PATH} />
              <ellipse cx={0} cy={0.8} rx={0.9} ry={0.7} />
              <ellipse cx={0} cy={3.3} rx={2.5} ry={2.7} />
              <path className={classes['spider-web__palps']} d={SPIDER_PALPS_PATH} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};
